import { AdminArticleDto } from '../generated-types/adminArticleDto';
import { ArticleAuthorDefaultsDto } from '../generated-types/articleAuthorDefaultsDto';
import { ArticleCardDto } from '../generated-types/articleCardDto';
import { ArticleDto } from '../generated-types/articleDto';
import { ArticleUpsertDto } from '../generated-types/articleUpsertDto';
import { AbstractApi } from './AbstractApi';

/**
 * An API to handle article requests.
 */
export default class ArticlesApi extends AbstractApi {
  private buildArticleFormData(article: ArticleUpsertDto, image?: File) {
    const formData = new FormData();
    formData.set('title', article.title);
    formData.set('authorFirstName', article.authorFirstName);
    formData.set('authorLastName', article.authorLastName);
    formData.set('altTag', article.altTag);
    formData.set('description', article.description);
    formData.set('content', article.content);
    if (image) formData.set('image', image);
    return formData;
  }

  private convertAdminArticleDates(article: AdminArticleDto) {
    article.created = new Date(article.created);
    article.lastUpdated = new Date(article.lastUpdated);
    return article;
  }

  /**
   * Get all the article cards
   * @returns The article cards to show
   */
  async GetArticleCards(): Promise<ArticleCardDto[]> {
    const response = await fetch(this.BuildUrl('articles'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    const dtos = (await response.json()) as unknown as ArticleCardDto[];

    // Convert the dates
    for (const dto of dtos) {
      dto.written = new Date(dto.written);
    }

    return dtos;
  }

  /**
   * Get the article matching the slug
   * @param slug The slug for the article
   * @returns The article cards to show. Returns null if not found
   */
  async GetArticle(slug: string): Promise<ArticleDto | null> {
    const response = await fetch(this.BuildUrl(`articles/${slug}`), {
      method: 'GET',
    });

    // The article matching the slug was not found
    if (response.status == 404) return null;

    // An error occurred
    if (!response.ok) throw Error();

    // We found a matching article
    const dto = (await response.json()) as unknown as ArticleDto;
    dto.date = new Date(dto.date); // Convert the date
    return dto;
  }

  /**
   * Get all articles for the admin page, including archived articles.
   */
  async GetAdminArticles(): Promise<AdminArticleDto[]> {
    const response = await this.authFetch(this.BuildUrl('articles/admin'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    const articles = (await response.json()) as unknown as AdminArticleDto[];
    return articles.map((a) => this.convertAdminArticleDates(a));
  }

  /**
   * Get article form defaults based on the currently logged-in admin.
   */
  async GetArticleAuthorDefaults(): Promise<ArticleAuthorDefaultsDto> {
    const response = await this.authFetch(
      this.BuildUrl('articles/admin/default-author'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return (await response.json()) as unknown as ArticleAuthorDefaultsDto;
  }

  /**
   * Create a new article.
   * @param article The article details
   * @param image The landscape article image
   */
  async CreateArticle(
    article: ArticleUpsertDto,
    image: File
  ): Promise<{
    code: number;
    body: string;
    article: AdminArticleDto | undefined;
  }> {
    const response = await this.authFetch(this.BuildUrl('articles/admin'), {
      method: 'POST',
      body: this.buildArticleFormData(article, image),
    });

    const body = await response.text();
    return {
      code: response.status,
      body: body,
      article: response.ok
        ? this.convertAdminArticleDates(JSON.parse(body) as AdminArticleDto)
        : undefined,
    };
  }

  /**
   * Update an existing article.
   * @param articleId The article id
   * @param article The article details
   * @param image Optional replacement landscape image
   */
  async UpdateArticle(
    articleId: string,
    article: ArticleUpsertDto,
    image?: File
  ): Promise<{
    code: number;
    body: string;
    article: AdminArticleDto | undefined;
  }> {
    const response = await this.authFetch(
      this.BuildUrl(`articles/admin/${articleId}`),
      {
        method: 'PUT',
        body: this.buildArticleFormData(article, image),
      }
    );

    const body = await response.text();
    return {
      code: response.status,
      body: body,
      article: response.ok
        ? this.convertAdminArticleDates(JSON.parse(body) as AdminArticleDto)
        : undefined,
    };
  }

  /**
   * Archive an article.
   * @param articleId The article id
   */
  async ArchiveArticle(articleId: string): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl(`articles/admin/${articleId}/archive`),
      {
        method: 'PUT',
      }
    );

    if (!response.ok) throw Error();
  }
}
